/**
 * Pre-upload QA panel — sits on the submission detail screen
 * (docs/mvp-intake-qa-spec.md §3).
 *
 * On mount it runs GET /api/admin/submissions/:jobId/:submittedAt/qa and
 * renders each QaRuleResult as a row (label, pass/fail/warn chip, detail).
 * This panel diagnoses only. Approving for the portal lives in the header
 * action bar as "Approve for Portal" — see ReportDetailScreen. It used to be a
 * button here, next to a header button labelled "Approve" that only moved the
 * status, which made it possible to approve a report twice and still have
 * nothing for the extension to read.
 *
 * Every finding is also attributed. Since submit stopped being gated on the
 * field tool's checks (field.ts `outstandingAtSubmit`), a failing rule can
 * mean two very different things — the inspector left site knowing about it,
 * or QA is the first to see it — and only the second is a surprise worth
 * chasing. The panel labels which, and lists what the inspector flagged that
 * QA either cannot see or now considers fixed.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CONSENT_INSPECTOR_ID_CARD_NUMBER,
  inspectorNotesForRule,
  reconcileOutstandingWithQa,
  type FieldReadiness,
  type OutstandingDisposition,
  type OutstandingEntry,
  type QaReport,
} from '@cer-autofill/shared';
import { apiErrorReason, fetchQaReport } from '../api.js';
import { Icon } from './Icon.js';
import {
  dispositionLabel,
  formatDateTime,
  qaChipClass,
  qaOriginChipClass,
  qaOriginLabel,
  qaOriginTitle,
  qaStatusLabel,
  readinessLabel,
} from '../format.js';

/** Rules whose evidence has a place on the page a "Show me" can land on. */
const SHOWABLE_RULES: ReadonlySet<string> = new Set([
  'coversheet-complete',
  'consent-captured',
  'hardware-photos',
  'isolator-details',
  'checklist-photos',
  'missing-clause-photos',
  'photo-timestamps',
  'nonconformance-assessments',
  'assessment-alignment',
]);

export function QaPanel({
  jobId,
  submittedAt,
  outstandingAtSubmit,
  presentPromptKeys = [],
  photoRevision = '',
  onShowRule,
  inspectorName,
  inspectorCard,
}: {
  inspectorName?: string;
  inspectorCard?: string | undefined;
  jobId: string;
  submittedAt: string;
  /** From the submission — absent on pre-Aug-2026 reports. See field.ts. */
  outstandingAtSubmit: readonly string[] | undefined;
  /**
   * Prompt keys of the photos on the report now. A reviewer upload clears the
   * matching missing-photo line without waiting for every other shot.
   */
  presentPromptKeys?: readonly string[];
  /** Changes when photos are added, removed or re-filed, so QA runs again. */
  photoRevision?: string;
  /**
   * Deep-link from a finding to its evidence: a section jump, or (for the
   * assessment rules) the editor opened on the first offending clause.
   */
  onShowRule?: (ruleId: string) => void;
}) {
  const [report, setReport] = useState<QaReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    void fetchQaReport(jobId, submittedAt)
      .then(setReport)
      .catch((e: unknown) => setLoadError(apiErrorReason(e)))
      .finally(() => setLoading(false));
  }, [jobId, submittedAt]);

  useEffect(() => {
    load();
  }, [load, photoRevision]);

  // Cross-reference the inspector's own outstanding list against this QA run.
  // With no QA report yet, an empty rule set is the honest input: every entry
  // reads as "not covered by QA" rather than as silently resolved.
  const { readiness, entries, originByRuleId } = useMemo(
    () => reconcileOutstandingWithQa(outstandingAtSubmit, report?.results ?? [], presentPromptKeys),
    [outstandingAtSubmit, presentPromptKeys, report],
  );

  // The inspector's own list does not depend on QA having run, so it stays on
  // screen through the loading and error branches — a QA outage should not
  // hide the one record of what came back from site incomplete.
  if (loading && !report) {
    return (
      <div className="card">
        <h3>Pre-upload QA</h3>
        <FieldReadinessSummary readiness={readiness} entries={entries} qaRan={false} />
        <p className="loading">Running QA checks…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="card">
        <h3>Pre-upload QA</h3>
        <FieldReadinessSummary readiness={readiness} entries={entries} qaRan={false} />
        <p className="error">QA check failed to run: {loadError}</p>
        <button type="button" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  if (!report) return null;

  const failing = report.results.filter((r) => r.status === 'fail');
  const warning = report.results.filter((r) => r.status === 'warn');
  const newToQa = [...failing, ...warning].filter(
    (r) => originByRuleId.get(r.ruleId) === 'qa-found',
  );

  return (
    <div className="card">
      <div className="qa-panel__header">
        <h3>Pre-upload QA</h3>
        <button type="button" disabled={loading} onClick={load}>
          {loading ? 'Re-running…' : 'Re-run QA'}
        </button>
      </div>

      {/* Overall summary ─────────────────────────────────────────────── */}
      <div className={`qa-overall qa-overall--${report.overall}`}>
        <span className={qaChipClass(report.overall)}>
          Overall: {qaStatusLabel(report.overall).toUpperCase()}
        </span>
        <span className="qa-overall__meta">
          {report.results.length} checks · generated {formatDateTime(report.generatedAt)}
        </span>
      </div>

      <FieldReadinessSummary readiness={readiness} entries={entries} qaRan />

      {newToQa.length > 0 && !readiness.legacy && (
        <p className="qa-new-findings">
          {newToQa.length} finding{newToQa.length === 1 ? ' was' : 's were'} not flagged at submit:{' '}
          {newToQa.map((r) => r.label).join(', ')}.
        </p>
      )}

      {/* Rule rows ───────────────────────────────────────────────────── */}
      <div className="qa-rules">
        {report.results.map((r) => {
          const origin = originByRuleId.get(r.ruleId);
          const notes = inspectorNotesForRule(entries, r.ruleId).filter((note) => {
            if (r.status === 'pass') return true;
            return entries.find((entry) => entry.issue === note)?.disposition !== 'resolved';
          });
          return (
            <div key={r.ruleId} className="qa-rule">
              <div className="qa-rule__head">
                <span className="qa-rule__label">{r.label}</span>
                {origin && (
                  <span className={qaOriginChipClass(origin)} title={qaOriginTitle(origin)}>
                    {/* Only the surprise gets a glyph — the same alert mark the
                        Jobs KPIs use for "needs attention". */}
                    {origin === 'qa-found' && <Icon name="alert" />}
                    {qaOriginLabel(origin)}
                  </span>
                )}
                <span className={qaChipClass(r.status)}>{qaStatusLabel(r.status)}</span>
                {onShowRule && r.status !== 'pass' && SHOWABLE_RULES.has(r.ruleId) && (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => onShowRule(r.ruleId)}
                    title="Jump to where this finding lives on the report."
                  >
                    Show me
                  </button>
                )}
              </div>
              {r.detail && <div className="qa-rule__detail">{r.detail}</div>}
              {/* Only where it adds something: on a rule QA now passes, the
                  inspector's note is the useful part (it was fixed after
                  submit); on a failing one it says who already knew. */}
              {notes.length > 0 && (
                <div className="qa-rule__inspector-note">
                  {r.status === 'pass'
                    ? 'Flagged at submit, clean now:'
                    : 'Inspector noted at submit:'}
                  <ul>
                    {notes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* neverFill hard rule — always visible next to the approve action. */}
      <div className="banner banner--warning banner--neverfill" role="alert">
        The portal&apos;s final &lsquo;Initial findings assessment&rsquo; must NEVER be selected —
        it alerts CER that a report has been generated
      </div>

      {/*
       * This panel diagnoses; it no longer approves.
       *
       * It used to carry its own "Approve for upload" button while the header
       * bar carried a button labelled "Approve" that only moved the status.
       * Two controls, one word, different consequences — and the one that
       * actually made a report reachable by the extension was the one buried
       * here. A report was approved twice and still could not be pushed.
       *
       * The action now lives in the header bar as "Approve for Portal",
       * alongside "Finalise", where the difference between them is visible.
       */}
      <div className="qa-approve">
        <p className="qa-approve__account">
          Inspector: {inspectorName ?? 'Not recorded'}
          {inspectorCard && inspectorCard !== CONSENT_INSPECTOR_ID_CARD_NUMBER
            ? ` (${inspectorCard})`
            : ''}
        </p>
        {report.overall === 'fail' ? (
          <p className="qa-approve__blocked">
            {failing.map((r) => r.label).join(', ')} {failing.length === 1 ? 'is' : 'are'} failing.
            You can still use <strong>Approve for Portal</strong> above — the CER portal runs its
            own validation and will not accept an incomplete submission.
          </p>
        ) : report.overall === 'warn' ? (
          <p className="qa-approve__blocked">
            {warning.length} warning{warning.length === 1 ? '' : 's'}. Use{' '}
            <strong>Approve for Portal</strong> above when you are satisfied.
          </p>
        ) : (
          <p>
            QA passed. Select <strong>Approve for Portal</strong> when ready.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * What arrived from site, before QA had an opinion on it.
 *
 * Entries whose QA rule is failing are already labelled on the rule rows
 * above, and resolved ones sit under the rule that now passes — so this lists
 * only the entries QA has nothing to say about, which are otherwise invisible
 * on this screen.
 */
function FieldReadinessSummary({
  readiness,
  entries,
  qaRan,
}: {
  readiness: FieldReadiness;
  entries: readonly OutstandingEntry[];
  /** False while QA is loading or errored — dispositions are meaningless then. */
  qaRan: boolean;
}) {
  if (readiness.legacy) {
    return (
      <p className="field-readiness field-readiness--legacy">
        Outstanding field checks were not recorded for this submission.
      </p>
    );
  }

  if (readiness.state === 'complete') {
    return (
      <p className="field-readiness field-readiness--complete">
        The field tool considered this report complete at submit — anything QA flags below is new.
      </p>
    );
  }

  const counts = new Map<OutstandingDisposition, number>();
  for (const e of entries) counts.set(e.disposition, (counts.get(e.disposition) ?? 0) + 1);
  // Without a QA run there is nothing to cross-reference against, so show the
  // inspector's list whole rather than sorting it into dispositions we cannot
  // yet justify.
  const listed = (
    qaRan
      ? entries.filter(
          (e) => e.disposition === 'not-qa-checked' || e.disposition === 'unclassified',
        )
      : entries
  ).filter((entry) => entry.disposition !== 'resolved');

  return (
    <div className="field-readiness field-readiness--needs-work">
      <div className="field-readiness__head">
        <strong>From the field: {readinessLabel(readiness)}</strong>
        {qaRan && (
          <span className="field-readiness__breakdown">
            {[...counts.entries()]
              .map(([disposition, n]) => `${n} ${dispositionLabel(disposition)}`)
              .join(' · ')}
          </span>
        )}
      </div>
      {listed.length > 0 && (
        <>
          <p className="field-readiness__lede">
            {qaRan
              ? 'The inspector flagged these and no QA rule covers them — they will not appear above:'
              : 'The inspector flagged these at submit:'}
          </p>
          <ul>
            {listed.map((e) => (
              <li key={e.issue}>{e.issue}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
